/**
 * @file Renders the sign-up password rules and their pass/fail state.
 */
import { useEffect, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Space, Typography, Tooltip } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useAuthenticator } from "@aws-amplify/ui-react";

import { W1 } from "@components/Constants";

import type { PasswordErrors } from "./helpers/validatePassword";

const { Text } = Typography;

const LABEL_MIN_LENGTH = "Has length of at least 8 characters";
const LABEL_REQUIREMENTS = "Password Requirements:";
const LABEL_SYMBOL_NUMBER = "Contains at least 1 number";
const LABEL_SYMBOL_LOWERCASE = "Contains at least 1 lowercase letter";
const LABEL_SYMBOL_UPPERCASE = "Contains at least 1 uppercase letter";
const LABEL_SYMBOL_SPECIAL_LIST = "^ $ * . [ ] { } ( ) ? - \" ! @ # % & / \\ , > < ' : ; | _ ~ ` + =";
const LABEL_SYMBOL_SPECIAL_PREFIX = "Contains at least 1 ";
const LABEL_SYMBOL_SPECIAL_POSTFIX = "special character ";

type RequirementItem = {
  key: keyof PasswordErrors;
  label: ReactNode;
};

/**
 * Lists the sign-up password rules, marking each one as met or unmet
 * against the Authenticator's current validation errors.
 */
const PasswordRequirements = (): JSX.Element => {
  const [ errors, setErrors ] = useState<PasswordErrors>({});

  const { validationErrors } = useAuthenticator();

  useEffect(() => {
    setErrors(validationErrors as PasswordErrors);
  }, [ validationErrors ]);

  /**
   * Returns the check icon's color and opacity for a rule, based on
   * whether the password is empty or the rule is unmet.
   */
  const getIconStyle = (key: keyof PasswordErrors): CSSProperties => {
    const hasNoPassword = !!errors.passwordRequired;

    if (hasNoPassword) {
      return { color: "grey", opacity: 0.5, width: 18 };
    }

    const isRuleNotMatched = !!errors[key];

    const style = isRuleNotMatched
      ? { color: "grey", opacity: 0.5, width: 18 }
      : { color: "green", width: 18 };

    return style;
  };

  const items: RequirementItem[] = [
    {
      key: "passwordLength",
      label: <Text>{LABEL_MIN_LENGTH}</Text>,
    },
    {
      key: "passwordNumberSymbol",
      label: <Text>{LABEL_SYMBOL_NUMBER}</Text>,
    },
    {
      key: "passwordSpecialSymbol",
      label: (
        <Text>
          {LABEL_SYMBOL_SPECIAL_PREFIX}
          <Tooltip title={LABEL_SYMBOL_SPECIAL_LIST}>
            <Text underline>
              {LABEL_SYMBOL_SPECIAL_POSTFIX}
            </Text>
          </Tooltip>
        </Text>
      ),
    },
    {
      key: "passwordUppercaseSymbol",
      label: <Text>{LABEL_SYMBOL_UPPERCASE}</Text>,
    },
    {
      key: "passwordLowercaseSymbol",
      label: <Text>{LABEL_SYMBOL_LOWERCASE}</Text>,
    },
  ];

  return (
    <Space
      style={{ marginBottom: W1 }}
      direction="vertical"
    >
      <Text>
        {LABEL_REQUIREMENTS}
      </Text>

      {
        items.map(({ key, label }) => (
          <Space key={key}>
            <CheckCircleOutlined
              style={{ ...getIconStyle(key) }}
            />

            <Text>
              {label}
            </Text>
          </Space>
        ))
      }
    </Space>
  );
};

export default PasswordRequirements;
